import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from "lucide-react";

// ============================================================
// CONTACT US — React component for Text2SQL · Uplatz AI
// Using existing project colors and fonts
// ============================================================

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    topic: "Product support",
    message: ""
  });
  const [status, setStatus] = useState({
    type: null, // 'success' | 'error' | 'info' | null
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: 'info', message: "Sending your message..." });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_ROOT}/api/auth/contact-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          topic: formData.topic,
          message: formData.message
        })
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || data.message || "Failed to send message");
      }

      setStatus({
        type: 'success',
        message: "Thank you! Your message has been sent successfully. We will get back to you shortly."
      });
      setFormData({ name: "", email: "", topic: "Product support", message: "" });
    } catch (err) {
      setStatus({
        type: 'error',
        message: err instanceof Error ? err.message : "Something went wrong. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6">
          <span className="text-indigo-400 font-mono text-sm font-bold tracking-wider uppercase">Contact us</span>
          <h1 className="text-4xl md:text-5xl font-extrabold mt-4 mb-6">Talk to the team behind Text2SQL</h1>
          <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Questions about Text2SQL, billing, a partnership, or a custom AI build? Send us a note and we'll get back to you.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Send us a message</h2>
            <p className="text-slate-600 text-sm mb-6">Pick a topic so it reaches the right person faster.</p>

            {status.type && (
              <div className={`p-4 rounded-xl mb-6 text-sm font-medium ${
                status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                status.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                {status.type === 'success' && <CheckCircle className="w-5 h-5 inline mr-2" />}
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">Work email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <label htmlFor="topic" className="block text-sm font-semibold text-slate-700 mb-1.5">Topic</label>
                <select
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors bg-white"
                >
                  <option>Product support</option>
                  <option>Billing &amp; accounts</option>
                  <option>Partnership</option>
                  <option>Custom AI build</option>
                  <option>Something else</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors resize-none"
                  placeholder="How can we help?"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
              <span className="text-indigo-600 font-mono text-xs font-bold tracking-wider uppercase">Email &amp; response time</span>
              <h3 className="text-xl font-bold text-slate-900 mt-2 mb-1">support@uplatz.ai</h3>
              <div className="flex items-center gap-2 text-emerald-700 font-mono text-sm font-medium mt-2">
                <span className="w-2 h-2 bg-emerald-600 rounded-full inline-block"></span>
                We reply within 1 business day
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Contact details</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Email</div>
                    <div className="text-slate-600 text-sm">support@uplatz.com</div>
                    <div className="text-slate-600 text-sm">info@uplatz.com</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Phone</div>
                    <div className="text-slate-600 text-sm">+1 (555) 123-4567</div>
                    <div className="text-slate-500 text-xs mt-0.5">Mon-Fri from 8am to 5pm</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Office</div>
                    <div className="text-slate-600 text-sm">
                      123 Innovation Drive<br />
                      Tech Valley, CA 94043<br />
                      United States
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Build CTA */}
            <div className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -bottom-1/2 -right-1/4 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-3xl" />
              </div>
              <div className="relative">
                <h3 className="text-xl font-bold mb-2">Need something custom?</h3>
                <p className="text-slate-300 text-sm mb-5">
                  Book a free 30-minute call to scope a bespoke AI build for your team.
                </p>
                <a
                  href="https://uplatz.ai/#build"
                  className="inline-block bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                >
                  Book a consultation →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;